import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, ArrowUpRight, Film } from "lucide-react";
import { Link } from "react-router-dom";
import { readCache, fetchWithCache } from "@/lib/swrCache";

// Extracts a still thumbnail out of common Bunny/YouTube/Vimeo iframe URLs.
// Falls back to `thumbnail_url` field. If neither, returns null.
function derivePoster(v) {
  if (v.thumbnail_url) return v.thumbnail_url;
  const u = v.video_url || "";
  // Bunny Stream: both iframe.mediadelivery.net/embed/... and player.mediadelivery.net/play/... work
  const bunny = u.match(/mediadelivery\.net\/(?:embed|play)\/(\d+)\/([a-f0-9-]+)/i);
  if (bunny) return `https://vz-${bunny[1]}.b-cdn.net/${bunny[2]}/thumbnail.jpg`;
  const yt = u.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|watch\?v=))([\w-]{6,})/);
  if (yt) return `https://i.ytimg.com/vi/${yt[1]}/maxresdefault.jpg`;
  return null;
}

// Adds/overrides autoplay=true on the iframe URL when the video becomes active.
function withAutoplay(url, autoplay = true) {
  if (!url) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("autoplay", autoplay ? "true" : "false");
    return u.toString();
  } catch {
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}autoplay=${autoplay ? "true" : "false"}`;
  }
}

function PlaylistItem({ v, active, onSelect, index }) {
  const poster = derivePoster(v);
  return (
    <button
      onClick={onSelect}
      className={`group relative w-full text-left rounded-2xl overflow-hidden border transition-colors duration-300 ${
        active
          ? "border-primary/60 bg-primary/[0.06] shadow-[0_20px_60px_-30px_hsl(var(--primary)/0.55)]"
          : "border-border/60 bg-card/40 hover:border-primary/30 hover:bg-card/70"
      }`}
      data-testid={`showcase-playlist-item-${v.id}`}
    >
      {/* Active accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300 ${active ? "bg-primary" : "bg-transparent"}`} />

      <div className="flex gap-3 p-3 pl-4">
        {/* thumbnail */}
        <div className="relative shrink-0 w-24 h-16 md:w-28 md:h-[72px] rounded-lg overflow-hidden bg-gradient-to-br from-primary/20 to-secondary/20">
          {poster ? (
            <img
              src={poster}
              alt=""
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <div className="w-full h-full grid place-items-center">
              <Film className="w-5 h-5 text-primary/70" />
            </div>
          )}
          <div className={`absolute inset-0 grid place-items-center transition-colors duration-300 ${active ? "bg-primary/40" : "bg-black/30 group-hover:bg-black/45"}`}>
            <div className={`w-8 h-8 rounded-full grid place-items-center transition-transform duration-300 ${active ? "bg-primary text-primary-foreground scale-110" : "bg-white/90 text-black group-hover:scale-105"}`}>
              <Play className="w-3.5 h-3.5 fill-current" />
            </div>
          </div>
          {v.duration && (
            <span className="absolute bottom-1 right-1 text-[10px] font-mono bg-black/80 text-white px-1.5 py-0.5 rounded">
              {v.duration}
            </span>
          )}
        </div>

        {/* meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-mono text-muted-foreground">{String(index + 1).padStart(2, "0")}</span>
            {v.tag && (
              <span className="text-[9px] font-mono uppercase tracking-widest text-primary px-1.5 py-0.5 rounded-full bg-primary/10 border border-primary/20">
                {v.tag}
              </span>
            )}
          </div>
          <div className={`font-heading font-semibold text-sm leading-snug line-clamp-2 transition-colors ${active ? "text-foreground" : "text-foreground/85 group-hover:text-foreground"}`}>
            {v.title}
          </div>
          {v.subtitle && (
            <div className="mt-1 text-[11px] text-muted-foreground line-clamp-1">{v.subtitle}</div>
          )}
        </div>
      </div>

      {/* active progress hint */}
      {active && (
        <motion.div
          layoutId="showcase-active-underline"
          className="h-0.5 bg-gradient-to-r from-primary via-secondary to-primary"
          transition={{ type: "spring", stiffness: 200, damping: 30 }}
        />
      )}
    </button>
  );
}

export default function VideoShowcase() {
  // Hydrate synchronously from sessionStorage so returning visitors see the
  // player + playlist paint immediately without waiting for the API.
  const [videos, setVideos] = useState(() => readCache("/showcase-videos")?.data || []);
  const [activeIdx, setActiveIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    fetchWithCache("/showcase-videos", (fresh) => setVideos(fresh || []))
      .catch(() => { /* keep whatever is already in state */ });
  }, []);

  const active = videos[activeIdx];
  const posterActive = useMemo(() => (active ? derivePoster(active) : null), [active]);

  const selectVideo = (i) => {
    setActiveIdx(i);
    setPlaying(true);
    // scroll the playlist so the newly-active item is centred (on mobile)
    const el = listRef.current?.querySelector(`[data-testid="showcase-playlist-item-${videos[i]?.id}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  if (!videos.length) return null;

  return (
    <section className="relative py-16 md:py-24" data-testid="video-showcase-section">
      {/* Backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[80%] max-w-[900px] h-[420px] rounded-full bg-primary/10 blur-[140px]" />
        <div className="absolute bottom-0 right-10 w-[420px] h-[420px] rounded-full bg-secondary/10 blur-[130px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Heading */}
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <div className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground">Video Showcase</div>
            <h2 className="mt-4 font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              See our work <span className="text-primary">in motion.</span>
            </h2>
            <p className="mt-4 text-muted-foreground text-sm md:text-base max-w-xl">
              Product walkthroughs, live agent demos and customer story reels — hand-picked from the CloudLab reel.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
            <Link to="/portfolio" className="rounded-full px-3.5 py-2 border border-border hover:border-primary hover:text-foreground transition-colors inline-flex items-center gap-1.5" data-testid="showcase-view-portfolio">
              View full portfolio <ArrowUpRight className="w-3 h-3" />
            </Link>
            <span className="w-px h-4 bg-border" />
            <span className="inline-flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              {videos.length} videos · autoplay next
            </span>
          </div>
        </div>

        {/* Player + Playlist */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.65fr)_minmax(0,1fr)] gap-6 lg:gap-8">
          {/* ---- Player ---- */}
          <div className="relative rounded-3xl overflow-hidden border border-border/70 bg-black/90 shadow-[0_60px_120px_-40px_hsl(var(--primary)/0.35)]">
            {/* aspect wrapper 16:9 */}
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={active?.id || activeIdx}
                  initial={{ opacity: 0, scale: 1.02 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0"
                >
                  {playing ? (
                    <iframe
                      key={`iframe-${active.id}`}
                      title={active.title}
                      src={withAutoplay(active.video_url, true)}
                      loading="lazy"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full border-0"
                      data-testid="showcase-video-iframe"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPlaying(true)}
                      className="group absolute inset-0 w-full h-full block"
                      data-testid="showcase-play-btn"
                    >
                      {posterActive ? (
                        <img src={posterActive} alt={active.title} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-black to-secondary/40" />
                      )}
                      {/* dim overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/0" />
                      {/* Big play */}
                      <div className="absolute inset-0 grid place-items-center">
                        <span className="relative flex h-20 w-20 md:h-24 md:w-24 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-[0_25px_60px_-15px_hsl(var(--primary)/0.9)] group-hover:scale-110 transition-transform duration-500">
                          <span className="absolute inset-0 rounded-full bg-primary/40 animate-ping" />
                          <Play className="w-8 h-8 md:w-10 md:h-10 fill-current relative z-10 translate-x-0.5" />
                        </span>
                      </div>
                      {/* bottom meta */}
                      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7 flex items-end justify-between gap-4 text-white">
                        <div className="min-w-0">
                          {active.tag && (
                            <span className="inline-block text-[10px] font-mono uppercase tracking-widest bg-white/10 backdrop-blur px-2 py-1 rounded-full border border-white/20 mb-2">
                              {active.tag}
                            </span>
                          )}
                          <div className="font-heading font-bold text-xl md:text-2xl leading-tight truncate">{active.title}</div>
                          {active.subtitle && (
                            <div className="mt-1 text-xs md:text-sm text-white/70 line-clamp-2 max-w-xl">{active.subtitle}</div>
                          )}
                        </div>
                        {active.duration && (
                          <div className="shrink-0 text-xs font-mono bg-black/60 px-2.5 py-1 rounded-full">{active.duration}</div>
                        )}
                      </div>
                    </button>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Below-player bar */}
            <div className="flex items-center justify-between gap-3 p-4 md:p-5 border-t border-white/10 bg-black/50 text-white">
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Now playing</div>
                <div className="mt-0.5 font-heading font-semibold truncate">{active.title}</div>
              </div>
              {active.project_link && (
                <Link
                  to={active.project_link}
                  className="shrink-0 rounded-full px-4 py-2 bg-primary text-primary-foreground text-xs font-medium inline-flex items-center gap-1.5 hover:opacity-90 transition-opacity"
                  data-testid="showcase-project-link"
                >
                  View project <ArrowUpRight className="w-3.5 h-3.5" />
                </Link>
              )}
            </div>
          </div>

          {/* ---- Playlist ---- */}
          <div className="relative">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Playlist</div>
              <div className="text-[10px] font-mono text-muted-foreground">{activeIdx + 1} / {videos.length}</div>
            </div>
            {/* Fixed-height clipping wrapper — always shows ~4 items on desktop, scrolls inside */}
            <div className="relative">
              <div
                ref={listRef}
                className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-y-auto lg:h-[464px] pb-2 lg:pb-2 pr-1 lg:pr-2 no-scrollbar lg:showcase-scrollbar snap-x lg:snap-y scroll-smooth overscroll-contain"
                data-testid="showcase-playlist"
              >
                {videos.map((v, i) => (
                  <div key={v.id} className="shrink-0 w-[85%] sm:w-[70%] lg:w-auto snap-start">
                    <PlaylistItem v={v} active={i === activeIdx} onSelect={() => selectVideo(i)} index={i} />
                  </div>
                ))}
              </div>
              {/* Bottom fade — hints at scrollable content when there are more than 4 videos */}
              {videos.length > 4 && (
                <div aria-hidden className="hidden lg:block pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-background to-transparent rounded-b-lg" />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
